import { useState, useEffect } from 'react';
import { MessageSquare, Save, RefreshCw, Phone, Calendar, CheckCircle2, AlertTriangle, Edit2 } from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Messages SMS par défaut
const DEFAULT_SMS_TEMPLATES = {
  lead_notification: {
    id: 'lead_notification',
    nom: 'Notification nouveau lead',
    description: 'SMS envoyé automatiquement quand un lead arrive via formulaire',
    message: "Bonjour {prenom}, nous avons bien reçu votre demande concernant l'adaptation de votre logement. Un conseiller Sani-Adapt va vous appeler très prochainement. À bientôt !",
    variables: ['prenom', 'nom'],
    actif: true,
    declencheur: 'Nouveau lead zone OK'
  },
  rdv_confirmation: {
    id: 'rdv_confirmation',
    nom: 'Confirmation RDV',
    description: 'SMS envoyé quand un RDV R1 est pris',
    message: "Bonjour {prenom}, votre RDV avec notre technicien est confirmé le {date_rdv} à {heure_rdv}. Adresse: {adresse}. À bientôt !",
    variables: ['prenom', 'date_rdv', 'heure_rdv', 'adresse'],
    actif: true,
    declencheur: 'RDV R1 pris'
  },
  rdv_rappel_j1: {
    id: 'rdv_rappel_j1',
    nom: 'Rappel RDV J-1',
    description: 'SMS de rappel envoyé la veille du RDV',
    message: "Bonjour {prenom}, petit rappel pour votre RDV de demain à {heure_rdv}. Notre technicien {technicien} viendra à l'adresse indiquée. À demain !",
    variables: ['prenom', 'heure_rdv', 'technicien'],
    actif: true,
    declencheur: 'Veille RDV'
  },
  tech_rappel_j1: {
    id: 'tech_rappel_j1',
    nom: 'Rappel Technicien J-1',
    description: 'SMS envoyé au technicien la veille de ses RDV',
    message: "Bonjour {technicien}, rappel de vos RDV de demain:\n{liste_rdv}\nBonne journée !",
    variables: ['technicien', 'liste_rdv'],
    actif: true,
    declencheur: 'Veille RDV (18h)'
  }
};

export default function SMSConfigPanel() {
  const [templates, setTemplates] = useState(DEFAULT_SMS_TEMPLATES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [testNumber, setTestNumber] = useState('');
  const [testSending, setTestSending] = useState(false);

  // Charger les templates depuis MongoDB
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/config/sms-templates`);
      if (resp.ok) {
        const data = await resp.json();
        if (data.templates && Object.keys(data.templates).length > 0) {
          setTemplates(data.templates);
        }
      }
    } catch (e) {
      console.error('Erreur chargement templates SMS:', e);
    }
    setLoading(false);
  };

  const saveTemplates = async () => {
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/config/sms-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templates })
      });
      if (resp.ok) {
        alert('✅ Templates SMS sauvegardés');
        setEditingId(null);
      }
    } catch (e) {
      alert('❌ Erreur: ' + e.message);
    }
    setSaving(false);
  };

  const updateTemplate = (id, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  };

  const testSMS = async (templateId) => {
    if (!testNumber) {
      alert('Entrez un numéro de test');
      return;
    }
    
    setTestSending(true);
    try {
      const template = templates[templateId];
      // Remplacer les variables par des valeurs de test
      let message = template.message;
      template.variables.forEach(v => {
        message = message.replace(`{${v}}`, `[${v}]`);
      });
      
      const resp = await authFetch(`${API}/webhook/sms/test?to_number=${encodeURIComponent(testNumber)}&message=${encodeURIComponent(message)}`, {
        method: 'POST'
      });
      const data = await resp.json();
      
      if (data.success) {
        alert('✅ SMS de test envoyé !');
      } else {
        alert('❌ Erreur: ' + data.error);
      }
    } catch (e) {
      alert('❌ Erreur: ' + e.message);
    }
    setTestSending(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-emerald-400" />
            Configuration SMS Automatiques
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Personnalisez les messages SMS envoyés automatiquement
          </p>
        </div>
        <button
          onClick={saveTemplates}
          disabled={saving}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          Sauvegarder tout
        </button>
      </div>

      {/* Test SMS */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
          <Phone className="w-4 h-4" />
          Tester l'envoi SMS
        </h4>
        <div className="flex gap-3">
          <input
            type="tel"
            value={testNumber}
            onChange={e => setTestNumber(e.target.value)}
            placeholder="06 XX XX XX XX"
            className="px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white flex-1"
          />
          <span className="text-slate-400 text-sm flex items-center">
            Numéro pour les tests
          </span>
        </div>
      </div>

      {/* Liste des templates */}
      <div className="space-y-4">
        {Object.values(templates).map(template => (
          <div
            key={template.id}
            className={`bg-slate-800/50 border rounded-xl p-4 ${
              editingId === template.id ? 'border-emerald-500' : 'border-slate-700'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Header */}
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-3 h-3 rounded-full ${template.actif ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                  <h3 className="font-medium text-white">{template.nom}</h3>
                  <span className="text-xs bg-slate-700 px-2 py-0.5 rounded text-slate-400">
                    {template.declencheur}
                  </span>
                </div>
                
                <p className="text-sm text-slate-400 mb-3">{template.description}</p>

                {/* Édition du message */}
                {editingId === template.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-400">Message SMS</label>
                      <textarea
                        value={template.message}
                        onChange={e => updateTemplate(template.id, 'message', e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm mt-1"
                        rows={4}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">Variables disponibles:</span>
                      {template.variables.map(v => (
                        <span key={v} className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                          {`{${v}}`}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={template.actif}
                          onChange={e => updateTemplate(template.id, 'actif', e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm">Actif</span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-900/50 rounded-lg p-3">
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{template.message}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {template.variables.map(v => (
                        <span key={v} className="text-xs bg-slate-700 text-slate-400 px-2 py-0.5 rounded">
                          {`{${v}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => setEditingId(editingId === template.id ? null : template.id)}
                  className="p-2 hover:bg-slate-700 rounded-lg"
                >
                  {editingId === template.id ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <Edit2 className="w-4 h-4 text-slate-400" />
                  )}
                </button>
                <button
                  onClick={() => testSMS(template.id)}
                  disabled={testSending || !testNumber}
                  className="p-2 hover:bg-blue-500/20 rounded-lg text-blue-400 disabled:opacity-50"
                  title="Envoyer un test"
                >
                  <Phone className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info Ringover */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <h4 className="text-amber-400 font-medium mb-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Note importante
        </h4>
        <p className="text-sm text-slate-400">
          Les SMS sont envoyés via l'API Ringover. Assurez-vous que votre compte Ringover dispose de crédits SMS suffisants.
          Les numéros doivent être au format français (06/07...).
        </p>
      </div>
    </div>
  );
}
